// Pagination progressive : une requête à la fois, annulée au changement de filtre.
class MlCollectionFeed extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-ref="main-collection-grid"]');
    this.control = this.querySelector('.ml-collection-feed__control');
    if (!this.grid || !this.control || !('IntersectionObserver' in window)) return;
    this.status = this.control.querySelector('[role="status"]');
    this.next = this.control.dataset.nextUrl;
    this.pages = new Set();
    this.failed = false;
    this.setAttribute('data-enhanced', '');
    this.control.hidden = false;
    this.addEventListener('click', this.onClick);
    // Suspendre immédiatement pour ne pas ajouter une ancienne page pendant un nouveau tri.
    document.addEventListener('filters:changed', this.suspend);
    window.addEventListener('popstate', this.suspend);
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting) && !this.failed) this.load();
    }, { rootMargin: '0px 0px 450px 0px' });
    this.updateStatus();
    if (this.next) this.observer.observe(this.control);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.request?.abort();
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('filters:changed', this.suspend);
    window.removeEventListener('popstate', this.suspend);
  }

  suspend = () => {
    this.failed = true;
    this.request?.abort();
    this.observer?.disconnect();
  };

  onClick = event => {
    const link = event.target.closest('[data-feed-next]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.failed = false;
    this.load();
  };

  updateStatus() {
    const count = this.grid.querySelectorAll('product-card').length;
    this.status.textContent = `${count} sur ${this.control.dataset.total} luminaires`;
    const link = this.control.querySelector('[data-feed-next]');
    if (link) {
      link.hidden = !this.next;
      if (this.next) link.href = this.next;
    }
  }

  async load() {
    if (!this.next || this.hasAttribute('data-loading') || !this.isConnected) return;
    const url = new URL(this.next, location.href);
    if (url.origin !== location.origin || this.pages.has(url.href)) return;
    this.setAttribute('data-loading', '');
    this.grid.setAttribute('aria-busy', 'true');
    this.status.textContent = 'Chargement des luminaires…';
    this.observer.unobserve(this.control);
    this.request = new AbortController();
    const { signal } = this.request;
    try {
      const response = await fetch(url.href, { signal });
      if (!response.ok) throw new Error('Page indisponible');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (signal.aborted || !this.isConnected) return;
      const incoming = html.querySelector('[data-ref="main-collection-grid"]');
      const control = html.querySelector('.ml-collection-feed__control');
      if (!incoming || !control) throw new Error('Grille de produits absente');
      const known = new Set([...this.grid.querySelectorAll('product-card')].map(e => e.dataset.productUrl));
      const children = [...incoming.children].filter(e => !e.matches('product-card') || !known.has(e.dataset.productUrl));
      if (!children.length) throw new Error('Aucun produit supplémentaire');
      // Les éléments personnalisés du thème initialisent leurs cartes et leurs galeries à l’insertion.
      // Adopter les nœuds seulement après leur rattachement à la section.
      // Le constructeur des galeries Fullstack a besoin de ce parent.
      this.grid.append(...children);
      this.pages.add(url.href);
      this.next = control.dataset.nextUrl;
      this.control.dataset.nextUrl = this.next;
      this.control.dataset.page = control.dataset.page;
      const oldPagination = this.querySelector('.pagination-wrapper');
      const newPagination = html.querySelector('ml-collection-feed > .pagination-wrapper');
      if (oldPagination && newPagination) oldPagination.replaceWith(document.importNode(newPagination, true));
      this.updateStatus();
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.failed = true;
        this.status.textContent = 'Le chargement a été interrompu. Vous pouvez réessayer.';
      }
    } finally {
      this.removeAttribute('data-loading');
      this.grid.removeAttribute('aria-busy');
      if (this.isConnected && this.next && !this.failed && !signal.aborted) this.observer.observe(this.control);
    }
  }
}
if (!customElements.get('ml-collection-feed')) customElements.define('ml-collection-feed', MlCollectionFeed);
